import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import "katex/dist/katex.min.css";
import { InlineMath, BlockMath } from "react-katex";
import { API_URL, getApiUrl } from "../config";
import { evaluate } from "mathjs";
import Fraction from "fraction.js";
import {
  Box,
  Button,
  Typography,
  CircularProgress,
  Tooltip,
  Chip,
} from "@mui/material";
import { styled, keyframes } from "@mui/material/styles";
import CalculateIcon from "@mui/icons-material/Calculate";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import SendIcon from "@mui/icons-material/Send";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import HistoryIcon from "@mui/icons-material/History";
import SchoolIcon from "@mui/icons-material/School";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import QuizIcon from "@mui/icons-material/Quiz";
import EditNoteIcon from "@mui/icons-material/EditNote";
import { toast } from "react-toastify";

// ── keyframes ──
const fadeUp = keyframes`from{opacity:0;transform:translateY(14px)}to{opacity:1;transform:translateY(0)}`;
const slideInL = keyframes`from{opacity:0;transform:translateX(-100%)}to{opacity:1;transform:translateX(0)}`;
const urgPulse = keyframes`0%,100%{box-shadow:0 0 0 0 rgba(239,68,68,.3)}50%{box-shadow:0 0 0 10px rgba(239,68,68,0)}`;
const optAnim = keyframes`from{opacity:0;transform:translateX(-8px)}to{opacity:1;transform:translateX(0)}`;
const popIn = keyframes`0%{opacity:0;transform:scale(.86) translateY(14px)}100%{opacity:1;transform:scale(1) translateY(0)}`;
const scanAnim = keyframes`0%{transform:translateX(-200%)}100%{transform:translateX(600%)}`;

// ── helpers ──
const toFrac = (n) => {
  try {
    return new Fraction(n).toFraction(true);
  } catch {
    return String(n);
  }
};
const renderContent = (text) => {
  if (!text) return null;
  return text
    .split(/(\$\$[\s\S]+?\$\$|\\\[[\s\S]+?\\\]|\\\([\s\S]+?\\\)|\$[^$]+\$)/g)
    .map((p, i) => {
      if (p.startsWith("$$") && p.endsWith("$$"))
        return (
          <div key={i} style={{ margin: "8px 0" }}>
            <BlockMath math={p.slice(2, -2)} />
          </div>
        );
      if (p.startsWith("\\[") && p.endsWith("\\]"))
        return (
          <div key={i} style={{ margin: "8px 0" }}>
            <BlockMath math={p.slice(2, -2)} />
          </div>
        );
      if (p.startsWith("\\(") && p.endsWith("\\)"))
        return <InlineMath key={i} math={p.slice(2, -2)} />;
      if (p.startsWith("$") && p.endsWith("$"))
        return <InlineMath key={i} math={p.slice(1, -1)} />;
      return (
        <span key={i} style={{ whiteSpace: "pre-line" }}>
          {p}
        </span>
      );
    });
};

// ── styled ──
const Root = styled(Box)({
  height: "100vh",
  background: "#f0f3f9",
  display: "flex",
  flexDirection: "column",
  fontFamily: "'Sora',sans-serif",
  overflow: "hidden",
});

const TopBar = styled(Box)({
  height: 62,
  background: "#07101f",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "0 20px",
  flexShrink: 0,
  zIndex: 200,
  borderBottom: "1px solid rgba(255,255,255,.07)",
  boxShadow: "0 4px 28px rgba(0,0,0,.28)",
});

const ModeBar = styled(Box)({
  background: "#0d1829",
  borderBottom: "1px solid rgba(255,255,255,.06)",
  padding: "9px 20px",
  display: "flex",
  alignItems: "center",
  gap: 10,
  flexShrink: 0,
});

const ModeBtn = styled(Button)(({ active }) => ({
  borderRadius: 9,
  textTransform: "none",
  fontFamily: "'Sora',sans-serif",
  fontWeight: 700,
  fontSize: ".8rem",
  padding: "7px 18px",
  transition: "all .22s ease",
  gap: 6,
  ...(active === "true"
    ? {
        background: "linear-gradient(105deg,#1d4ed8,#3b82f6)",
        color: "#fff",
        boxShadow: "0 4px 14px -4px rgba(37,99,235,.5)",
      }
    : {
        background: "rgba(255,255,255,.07)",
        color: "rgba(255,255,255,.5)",
        border: "1px solid rgba(255,255,255,.08)",
        "&:hover": { background: "rgba(255,255,255,.12)", color: "#fff" },
      }),
}));

const TimerChip = styled(Box)(({ urgent }) => ({
  display: "flex",
  alignItems: "center",
  gap: 7,
  background:
    urgent === "true" ? "rgba(239,68,68,.15)" : "rgba(255,255,255,.08)",
  border: `1.5px solid ${
    urgent === "true" ? "rgba(239,68,68,.45)" : "rgba(255,255,255,.1)"
  }`,
  borderRadius: 10,
  padding: "5px 14px",
  animation:
    urgent === "true" ? `${urgPulse} 1.4s ease-in-out infinite` : "none",
}));

const CalcSide = styled(Box)(({ open }) => ({
  width: open === "true" ? 278 : 0,
  minWidth: open === "true" ? 278 : 0,
  background: "#0d1829",
  borderRight: "1px solid rgba(255,255,255,.07)",
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
  transition:
    "width .32s cubic-bezier(.22,1,.36,1), min-width .32s cubic-bezier(.22,1,.36,1)",
  flexShrink: 0,
}));

const CalcInner = styled(Box)({
  width: 278,
  height: "100%",
  display: "flex",
  flexDirection: "column",
  padding: "16px 12px",
  overflowY: "auto",
  "&::-webkit-scrollbar": { width: 4 },
  "&::-webkit-scrollbar-thumb": {
    background: "rgba(255,255,255,.1)",
    borderRadius: 4,
  },
});

const CalcScreen = styled(Box)({
  background: "#060e1c",
  borderRadius: 11,
  padding: "12px 14px",
  marginBottom: 12,
  minHeight: 76,
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-end",
  justifyContent: "flex-end",
  border: "1px solid rgba(255,255,255,.08)",
  position: "relative",
  overflow: "hidden",
  "&::after": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: 1,
    background:
      "linear-gradient(90deg,transparent,rgba(96,165,250,.25),transparent)",
    animation: `${scanAnim} 4s ease-in-out infinite`,
  },
});

const CBtn = styled(Button)(({ cv }) => ({
  borderRadius: 8,
  minWidth: 0,
  padding: "9px 3px",
  fontFamily: "'Sora',sans-serif",
  fontWeight: 700,
  fontSize: ".78rem",
  textTransform: "none",
  transition: "all .15s ease",
  ...(cv === "op" && {
    background: "#1e3a70",
    color: "#93c5fd",
    "&:hover": { background: "#1d4ed8", transform: "translateY(-1px)" },
  }),
  ...(cv === "fn" && {
    background: "#0c3344",
    color: "#67e8f9",
    fontSize: ".69rem",
    "&:hover": { background: "#0e4d66", transform: "translateY(-1px)" },
  }),
  ...(cv === "eq" && {
    background: "linear-gradient(135deg,#1d4ed8,#3b82f6)",
    color: "#fff",
    boxShadow: "0 6px 16px -4px rgba(37,99,235,.5)",
    "&:hover": {
      transform: "translateY(-2px)",
      boxShadow: "0 10px 22px -4px rgba(37,99,235,.6)",
    },
  }),
  ...(cv === "cl" && {
    background: "#450a0a",
    color: "#fca5a5",
    "&:hover": { background: "#7f1d1d", transform: "translateY(-1px)" },
  }),
  ...(cv === "num" && {
    background: "#1e2d45",
    color: "#e2e8f0",
    "&:hover": { background: "#253553", transform: "translateY(-1px)" },
  }),
  ...(cv === "mod" && {
    background: "transparent",
    color: "#64748b",
    border: "1px solid rgba(255,255,255,.1)",
    fontSize: ".67rem",
    "&:hover": { background: "rgba(255,255,255,.06)" },
  }),
}));

const Body = styled(Box)({
  display: "flex",
  flex: 1,
  overflow: "hidden",
  minHeight: 0,
});
const MainArea = styled(Box)({
  flex: 1,
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
  minHeight: 0,
});

const ScrollPane = styled(Box)({
  flex: 1,
  overflowY: "auto",
  padding: "20px 24px",
  minHeight: 0,
  "&::-webkit-scrollbar": { width: 5 },
  "&::-webkit-scrollbar-track": { background: "transparent" },
  "&::-webkit-scrollbar-thumb": {
    background: "rgba(15,23,42,.13)",
    borderRadius: 6,
  },
  "&::-webkit-scrollbar-thumb:hover": { background: "rgba(15,23,42,.22)" },
});

const QCard = styled(Box)({
  background: "#fff",
  borderRadius: 18,
  border: "1.5px solid #e8edf5",
  padding: "26px 30px 22px",
  boxShadow: "0 4px 24px -8px rgba(15,23,42,.08)",
  animation: `${fadeUp} .3s cubic-bezier(.22,1,.36,1) both`,
  marginBottom: 16,
});

const OptBtn = styled(Box)(({ status }) => ({
  display: "flex",
  alignItems: "flex-start",
  gap: 12,
  padding: "13px 16px",
  borderRadius: 12,
  marginBottom: 10,
  transition: "all .2s ease",
  animation: `${optAnim} .26s cubic-bezier(.22,1,.36,1) both`,
  cursor: status === "correct" || status === "wrong" ? "default" : "pointer",
  ...(status === "selected" && {
    border: "2px solid #3b82f6",
    background: "#eff6ff",
  }),
  ...(status === "correct" && {
    border: "2px solid #22c55e",
    background: "#f0fdf4",
  }),
  ...(status === "wrong" && {
    border: "2px solid #ef4444",
    background: "#fff1f2",
  }),
  ...(status === "none" && {
    border: "2px solid #e8edf5",
    background: "#fafbff",
    "&:hover": {
      borderColor: "#93c5fd",
      background: "#f0f6ff",
      transform: "translateX(4px)",
    },
  }),
}));

const OptCircle = styled(Box)(({ status }) => ({
  width: 34,
  height: 34,
  borderRadius: "50%",
  flexShrink: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontWeight: 800,
  fontSize: ".82rem",
  transition: "all .2s ease",
  ...(status === "selected" && {
    background: "linear-gradient(135deg,#1d4ed8,#3b82f6)",
    color: "#fff",
    boxShadow: "0 4px 14px rgba(37,99,235,.35)",
  }),
  ...(status === "correct" && {
    background: "linear-gradient(135deg,#15803d,#22c55e)",
    color: "#fff",
    boxShadow: "0 4px 12px rgba(22,163,74,.3)",
  }),
  ...(status === "wrong" && {
    background: "linear-gradient(135deg,#dc2626,#ef4444)",
    color: "#fff",
    boxShadow: "0 4px 12px rgba(220,38,38,.3)",
  }),
  ...(status === "none" && { background: "#f1f5f9", color: "#64748b" }),
}));

const BottomNav = styled(Box)({
  background: "#fff",
  borderTop: "1.5px solid #e8edf5",
  padding: "12px 22px 14px",
  flexShrink: 0,
  boxShadow: "0 -4px 18px -4px rgba(15,23,42,.07)",
});

const NavDot = styled(Box)(({ isanswered, iscurrent, iswrong, iscorrect }) => ({
  width: 36,
  height: 36,
  borderRadius: 10,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontFamily: "'Sora',sans-serif",
  fontWeight: 700,
  fontSize: ".76rem",
  cursor: "pointer",
  transition: "all .18s ease",
  flexShrink: 0,
  border:
    iscurrent === "true"
      ? "2.5px solid #3b82f6"
      : iscorrect === "true"
      ? "2px solid #22c55e"
      : iswrong === "true"
      ? "2px solid #ef4444"
      : isanswered === "true"
      ? "2px solid #22c55e"
      : "2px solid #e2e8f0",
  background:
    iscurrent === "true"
      ? "#dbeafe"
      : iscorrect === "true"
      ? "#f0fdf4"
      : iswrong === "true"
      ? "#fff1f2"
      : isanswered === "true"
      ? "#f0fdf4"
      : "#fff",
  color:
    iscurrent === "true"
      ? "#1d4ed8"
      : iscorrect === "true"
      ? "#15803d"
      : iswrong === "true"
      ? "#dc2626"
      : isanswered === "true"
      ? "#15803d"
      : "#64748b",
  "&:hover": {
    transform: "scale(1.1)",
    boxShadow: "0 2px 8px rgba(59,130,246,.18)",
  },
}));

const ScoreRing = styled(Box)(({ pct }) => ({
  width: 108,
  height: 108,
  borderRadius: "50%",
  margin: "0 auto 18px",
  background: `conic-gradient(${
    parseInt(pct) >= 50 ? "#22c55e" : "#ef4444"
  } ${pct}%,#f1f5f9 0%)`,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  boxShadow: `0 8px 24px ${
    parseInt(pct) >= 50 ? "rgba(34,197,94,.22)" : "rgba(239,68,68,.18)"
  }`,
  position: "relative",
  "&::before": {
    content: '""',
    position: "absolute",
    inset: 10,
    borderRadius: "50%",
    background: "#fff",
  },
}));

// ── component ──
export default function WaecExamPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const token = JSON.parse(localStorage.getItem("authToken"));
  const subjectId = new URLSearchParams(location.search).get("subjectId");

  const [questions, setQuestions] = useState([]);
  const [selectedOptions, setSelectedOptions] = useState({});
  const [timeLeft, setTimeLeft] = useState(50 * 60);
  const [examYear, setExamYear] = useState(null);
  const [score, setScore] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [corrections, setCorrections] = useState([]);
  const [showCorrections, setShowCorrections] = useState(false);
  const [loadingQ, setLoadingQ] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [previewImg, setPreviewImg] = useState(null);
  const [examMode, setExamMode] = useState("objective");
  const [currentIdx, setCurrentIdx] = useState(0);
  const [calcOpen, setCalcOpen] = useState(false);
  const [showHist, setShowHist] = useState(false);

  const [disp, setDisp] = useState("");
  const [expr, setExpr] = useState("");
  const [ans, setAns] = useState(0);
  const [hist, setHist] = useState([]);
  const [angMode, setAngMode] = useState("DEG");
  const submittingRef = useRef(false);

  useEffect(() => {
    const link = document.createElement("link");
    link.href =
      "https://fonts.googleapis.com/css2?family=Sora:wght@300;400;500;600;700;800&family=DM+Serif+Display:ital@0;1&display=swap";
    link.rel = "stylesheet";
    document.head.appendChild(link);
  }, []);

  useEffect(() => {
     const API_URL = getApiUrl();
    if (!token?.access) {
      navigate("/student-login");
      return;
    }
    (async () => {
      setLoadingQ(true);
      try {
        const r = await fetch(
          `${API_URL}/desktop-exam-questions/${subjectId}/`,
          { headers: { Authorization: `Bearer ${token.access}` } }
        );
        if (r.status === 403) {
          toast.error("No active plan.");
          navigate("/waec-subject-page");
          return;
        }
        const d = await r.json();
        if (d.objective_time) {
          const [h, m, s] = d.objective_time.split(":").map(Number);
          setTimeLeft(h * 3600 + m * 60 + s);
        }
        setExamYear(d.year || null);
        setQuestions(d.questions || []);
      } catch {
        toast.error("Failed to load questions.");
        navigate("/nsubjects");
      } finally {
        setLoadingQ(false);
      }
    })();
    // eslint-disable-next-line
  }, [subjectId]);

  const doSubmit = useCallback(
    async (opts) => {
      if (submittingRef.current) return;
      submittingRef.current = true;
      setSubmitting(true);
      try {
        const r = await fetch(
          `${API_URL}/desktop-exam-submit/${subjectId}/submit/`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token.access}`,
            },
            body: JSON.stringify({ answers: opts }),
          }
        );
        let d;
        try {
          d = JSON.parse(await r.text());
        } catch {
          toast.error("Invalid response");
          return;
        }
        setScore({ correct: d.correct_count, total: d.total_questions });
        setCorrections(d.results || []);
        setShowModal(true);
      } catch {
        toast.error("Failed to submit exam.");
        navigate("/nsubjects");
      } finally {
        submittingRef.current = false;
        setSubmitting(false);
      }
    },
    [subjectId, token, navigate]
  );

  useEffect(() => {
    if (loadingQ || questions.length === 0) return;
    const id = setInterval(
      () =>
        setTimeLeft((p) => {
          if (p <= 1) {
            clearInterval(id);
            doSubmit(selectedOptions);
            return 0;
          }
          return p - 1;
        }),
      1000
    );
    return () => clearInterval(id);
    // eslint-disable-next-line
  }, [loadingQ, questions.length]);

  const corrMap = React.useMemo(() => {
    const m = {};
    (Array.isArray(corrections) ? corrections : []).forEach((c) => {
      m[c.question_id] = c;
    });
    return m;
  }, [corrections]);

  const h = Math.floor(timeLeft / 3600),
    m = Math.floor((timeLeft % 3600) / 60),
    s = timeLeft % 60;
  const timeStr =
    h > 0
      ? `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(
          s
        ).padStart(2, "0")}`
      : `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;

  const isUrgent = timeLeft <= 60,
    isWarn = timeLeft <= 300 && !isUrgent;
  const answered = Object.keys(selectedOptions).length,
    totalQ = questions.length,
    currentQ = questions[currentIdx];
  const pct = score ? Math.round((score.correct / score.total) * 100) : 0;

  const append = (v) => setDisp((p) => p + v),
    clearD = () => setDisp(""),
    delLast = () => setDisp((p) => p.slice(0, -1));
  const calc = useCallback(() => {
    try {
      let e = disp.replace(/ANS/g, ans);
      if (angMode === "DEG")
        e = e
          .replace(/sin\(([^)]*)\)/g, "sin(($1)*pi/180)")
          .replace(/cos\(([^)]*)\)/g, "cos(($1)*pi/180)")
          .replace(/tan\(([^)]*)\)/g, "tan(($1)*pi/180)");
      const res = evaluate(e);
      const frac = toFrac(res);
      setAns(res);
      setExpr(disp + " =");
      setDisp(frac);
      setHist((p) => [...p, `${disp} = ${frac}`]);
    } catch {
      setDisp("Error");
    }
  }, [disp, ans, angMode]);

  useEffect(() => {
    const hk = (e) => {
      if (!calcOpen) return;
      if ("0123456789+-*/().".includes(e.key)) append(e.key);
      if (e.key === "Enter") calc();
      if (e.key === "Backspace") delLast();
    };
    window.addEventListener("keydown", hk);
    return () => window.removeEventListener("keydown", hk);
  }, [calc, calcOpen]);

  const calcRows = [
    [
      { l: "sin", a: () => append("sin("), v: "fn" },
      { l: "cos", a: () => append("cos("), v: "fn" },
      { l: "tan", a: () => append("tan("), v: "fn" },
      { l: "log", a: () => append("log("), v: "fn" },
    ],
    [
      { l: "√", a: () => append("sqrt("), v: "fn" },
      { l: "x²", a: () => append("^2"), v: "fn" },
      { l: "xʸ", a: () => append("^"), v: "fn" },
      { l: "x!", a: () => append("!"), v: "fn" },
    ],
    [
      { l: "π", a: () => append("pi"), v: "fn" },
      { l: "e", a: () => append("e"), v: "fn" },
      { l: "ANS", a: () => append("ANS"), v: "fn" },
      { l: "( )", a: () => append("("), v: "fn" },
    ],
    [
      { l: "7", a: () => append("7"), v: "num" },
      { l: "8", a: () => append("8"), v: "num" },
      { l: "9", a: () => append("9"), v: "num" },
      { l: "÷", a: () => append("/"), v: "op" },
    ],
    [
      { l: "4", a: () => append("4"), v: "num" },
      { l: "5", a: () => append("5"), v: "num" },
      { l: "6", a: () => append("6"), v: "num" },
      { l: "×", a: () => append("*"), v: "op" },
    ],
    [
      { l: "1", a: () => append("1"), v: "num" },
      { l: "2", a: () => append("2"), v: "num" },
      { l: "3", a: () => append("3"), v: "num" },
      { l: "−", a: () => append("-"), v: "op" },
    ],
    [
      { l: "0", a: () => append("0"), v: "num" },
      { l: ".", a: () => append("."), v: "num" },
      { l: "⌫", a: delLast, v: "cl" },
      { l: "+", a: () => append("+"), v: "op" },
    ],
  ];

  const getOptStatus = (q, opt) => {
    if (!showCorrections)
      return selectedOptions[q.id] === opt.id ? "selected" : "none";
    const corr = corrMap[q.id];
    if (!corr) return "none";
    const correctLetter = Array.isArray(corr.correct_options)
      ? corr.correct_options
      : [corr.correct_options];
    if (correctLetter.includes(opt.option_letter)) return "correct";
    if (
      corr.selected_options === opt.option_letter &&
      corr.result === "incorrect"
    )
      return "wrong";
    return "none";
  };

  const switchMode = (mode) => {
    setExamMode(mode);
    setCurrentIdx(0);
    setShowCorrections(false);
  };

  if (loadingQ)
    return (
      <Root
        sx={{
          alignItems: "center",
          justifyContent: "center",
          gap: 2,
          background: "#07101f",
        }}
      >
        <CircularProgress size={48} thickness={4} sx={{ color: "#3b82f6" }} />
        <Typography
          sx={{
            fontFamily: "'Sora',sans-serif",
            color: "rgba(255,255,255,.5)",
            fontSize: ".9rem",
          }}
        >
          Loading your exam…
        </Typography>
      </Root>
    );

  return (
    <Root>
      {/* TOP BAR */}
      <TopBar>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box
            onClick={() => navigate(-1)}
            sx={{
              width: 34,
              height: 34,
              borderRadius: "9px",
              background: "rgba(255,255,255,.08)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              "&:hover": { background: "rgba(255,255,255,.15)" },
            }}
          >
            <ArrowBackIcon
              sx={{ color: "rgba(255,255,255,.6)", fontSize: 17 }}
            />
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Box
              sx={{
                width: 30,
                height: 30,
                borderRadius: "8px",
                background: "linear-gradient(135deg,#1d4ed8,#3b82f6)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <SchoolIcon sx={{ color: "#fff", fontSize: 16 }} />
            </Box>
            <Typography
              sx={{
                fontFamily: "'Sora',sans-serif",
                fontWeight: 700,
                color: "#e2e8f0",
                fontSize: ".85rem",
                display: { xs: "none", sm: "block" },
              }}
            >
              LiteUpper
            </Typography>
          </Box>
          <Tooltip
            title={calcOpen ? "Close Calculator" : "Open Calculator"}
            arrow
          >
            <Box
              onClick={() => setCalcOpen((o) => !o)}
              sx={{
                width: 36,
                height: 36,
                borderRadius: "9px",
                background: calcOpen
                  ? "linear-gradient(135deg,#1d4ed8,#3b82f6)"
                  : "rgba(255,255,255,.08)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                transition: "all .22s",
                boxShadow: calcOpen
                  ? "0 4px 14px -4px rgba(37,99,235,.5)"
                  : "none",
                "&:hover": {
                  background: calcOpen
                    ? "linear-gradient(135deg,#1e40af,#2563eb)"
                    : "rgba(255,255,255,.15)",
                },
              }}
            >
              <CalculateIcon
                sx={{
                  fontSize: 18,
                  color: calcOpen ? "#fff" : "rgba(255,255,255,.6)",
                }}
              />
            </Box>
          </Tooltip>
        </Box>

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Typography
            sx={{
              fontFamily: "'Sora',sans-serif",
              fontWeight: 700,
              color: "#e2e8f0",
              fontSize: ".86rem",
              lineHeight: 1.2,
            }}
          >
            {examYear ? `Year ${examYear}` : "Exam Session"}
          </Typography>
          <Typography
            sx={{
              fontFamily: "'Sora',sans-serif",
              fontSize: ".66rem",
              color: "rgba(255,255,255,.35)",
            }}
          >
            {answered}/{totalQ} answered
          </Typography>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <TimerChip urgent={isUrgent ? "true" : "false"}>
            <AccessTimeIcon
              sx={{
                fontSize: 15,
                color: isUrgent ? "#f87171" : isWarn ? "#fbbf24" : "#60a5fa",
              }}
            />
            <Typography
              sx={{
                fontFamily: "'Sora',sans-serif",
                fontWeight: 800,
                fontSize: ".88rem",
                color: isUrgent ? "#f87171" : isWarn ? "#fbbf24" : "#e2e8f0",
                letterSpacing: ".04em",
              }}
            >
              {timeStr}
            </Typography>
          </TimerChip>
          {!showCorrections ? (
            <Button
              onClick={() => doSubmit(selectedOptions)}
              disabled={submitting}
              endIcon={
                submitting ? (
                  <CircularProgress size={13} color="inherit" />
                ) : (
                  <SendIcon sx={{ fontSize: ".88rem !important" }} />
                )
              }
              sx={{
                borderRadius: 9,
                textTransform: "none",
                fontFamily: "'Sora',sans-serif",
                fontWeight: 700,
                fontSize: ".78rem",
                py: "6px",
                px: 1.8,
                background: "linear-gradient(105deg,#15803d,#22c55e)",
                boxShadow: "0 6px 16px -4px rgba(22,163,74,.45)",
                "&:hover": { transform: "translateY(-2px)" },
                "&.Mui-disabled": {
                  background: "#334155",
                  color: "rgba(255,255,255,.3)",
                },
                transition: "all .22s",
                color: "#fff",
              }}
              variant="contained"
            >
              {submitting ? "" : "Submit"}
            </Button>
          ) : (
            <Button
              onClick={() => navigate(`/ntheory?subjectId=${subjectId}`)}
              sx={{
                borderRadius: 9,
                textTransform: "none",
                fontFamily: "'Sora',sans-serif",
                fontWeight: 700,
                fontSize: ".76rem",
                py: "6px",
                px: 1.8,
                background: "linear-gradient(105deg,#7c3aed,#a78bfa)",
                boxShadow: "0 6px 16px -4px rgba(124,58,237,.45)",
                "&:hover": { transform: "translateY(-2px)" },
                transition: "all .22s",
                color: "#fff",
              }}
              variant="contained"
            >
              Theory →
            </Button>
          )}
        </Box>
      </TopBar>

      {/* MODE TOGGLE BAR */}
      <ModeBar>
        <ModeBtn
          active={examMode === "objective" ? "true" : "false"}
          onClick={() => switchMode("objective")}
          startIcon={<QuizIcon sx={{ fontSize: "1rem !important" }} />}
        >
          Objective
          {answered > 0 && (
            <Box
              component="span"
              sx={{
                ml: 0.5,
                background: "rgba(255,255,255,.15)",
                borderRadius: 100,
                px: 1,
                py: 0.1,
                fontSize: ".63rem",
                fontWeight: 700,
              }}
            >
              {answered}/{totalQ}
            </Box>
          )}
        </ModeBtn>
        <Box
          sx={{
            width: 1,
            height: 22,
            background: "rgba(255,255,255,.1)",
            mx: 0.5,
          }}
        />
        <ModeBtn
          active={examMode === "theory" ? "true" : "false"}
          onClick={() => switchMode("theory")}
          startIcon={<EditNoteIcon sx={{ fontSize: "1rem !important" }} />}
        >
          Theory
        </ModeBtn>
        <Box sx={{ flex: 1 }} />
        {/* Mini progress */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            background: "rgba(255,255,255,.05)",
            borderRadius: 8,
            px: 1.5,
            py: 0.8,
          }}
        >
          <Box
            sx={{
              height: 3,
              width: 80,
              background: "rgba(255,255,255,.1)",
              borderRadius: 100,
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                height: "100%",
                width: `${totalQ > 0 ? (answered / totalQ) * 100 : 0}%`,
                background: "linear-gradient(90deg,#3b82f6,#60a5fa)",
                borderRadius: 100,
                transition: "width .4s ease",
              }}
            />
          </Box>
          <Typography
            sx={{
              fontFamily: "'Sora',sans-serif",
              fontSize: ".65rem",
              color: "rgba(255,255,255,.38)",
              whiteSpace: "nowrap",
            }}
          >
            {Math.round(totalQ > 0 ? (answered / totalQ) * 100 : 0)}% done
          </Typography>
        </Box>
      </ModeBar>

      {/* BODY */}
      <Body>
        {/* CALC SIDEBAR */}
        <CalcSide open={calcOpen ? "true" : "false"}>
          {calcOpen && (
            <CalcInner
              sx={{
                animation: `${slideInL} .3s cubic-bezier(.22,1,.36,1) both`,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  mb: 1.8,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <CalculateIcon sx={{ color: "#60a5fa", fontSize: 16 }} />
                  <Typography
                    sx={{
                      fontFamily: "'Sora',sans-serif",
                      fontWeight: 700,
                      color: "#e2e8f0",
                      fontSize: ".82rem",
                    }}
                  >
                    Calculator
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", gap: 0.6 }}>
                  {["DEG", "RAD"].map((mode) => (
                    <CBtn
                      key={mode}
                      cv="mod"
                      onClick={() => setAngMode(mode)}
                      sx={{
                        background:
                          angMode === mode ? "#1e3a70" : "transparent",
                        color: angMode === mode ? "#93c5fd" : "#475569",
                      }}
                    >
                      {mode}
                    </CBtn>
                  ))}
                </Box>
              </Box>
              <CalcScreen>
                <Typography
                  sx={{
                    fontFamily: "'Sora',sans-serif",
                    fontSize: ".65rem",
                    color: "#334155",
                    mb: 0.3,
                    wordBreak: "break-all",
                    width: "100%",
                    textAlign: "right",
                  }}
                >
                  {expr || "\u00A0"}
                </Typography>
                <Typography
                  sx={{
                    fontFamily: "'Sora',sans-serif",
                    fontWeight: 800,
                    fontSize: disp.length > 14 ? ".95rem" : "1.5rem",
                    color: disp === "Error" ? "#f87171" : "#e2e8f0",
                    wordBreak: "break-all",
                    textAlign: "right",
                  }}
                >
                  {disp || "0"}
                </Typography>
              </CalcScreen>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: 0.7,
                  mb: 0.8,
                }}
              >
                <CBtn cv="cl" onClick={clearD} fullWidth>
                  AC
                </CBtn>
                <CBtn cv="fn" onClick={() => append("(")} fullWidth>
                  ( )
                </CBtn>
              </Box>
              {calcRows.map((row, ri) => (
                <Box
                  key={ri}
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "repeat(4,1fr)",
                    gap: "5px",
                    mb: "5px",
                  }}
                >
                  {row.map((b, bi) => (
                    <CBtn key={bi} cv={b.v} onClick={b.a} fullWidth>
                      {b.l}
                    </CBtn>
                  ))}
                </Box>
              ))}
              <Box sx={{ mt: "5px", mb: 1 }}>
                <CBtn
                  cv="eq"
                  onClick={calc}
                  fullWidth
                  sx={{ py: 1.15, fontSize: "1rem" }}
                >
                  =
                </CBtn>
              </Box>
              <Box
                onClick={() => setShowHist((h) => !h)}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  mt: 1,
                  mb: 0.5,
                  cursor: "pointer",
                  color: "#475569",
                  "&:hover": { color: "#64748b" },
                }}
              >
                <HistoryIcon sx={{ fontSize: 14 }} />
                <Typography
                  sx={{
                    fontFamily: "'Sora',sans-serif",
                    fontSize: ".67rem",
                    fontWeight: 600,
                  }}
                >
                  {showHist ? "Hide" : "Show"} History
                </Typography>
              </Box>
              {showHist && hist.length > 0 && (
                <Box
                  sx={{
                    background: "#060e1c",
                    borderRadius: 9,
                    p: "8px 10px",
                    maxHeight: 100,
                    overflowY: "auto",
                  }}
                >
                  {hist
                    .slice(-8)
                    .reverse()
                    .map((hh, i) => (
                      <Typography
                        key={i}
                        sx={{
                          fontFamily: "'Sora',sans-serif",
                          fontSize: ".65rem",
                          color: "#475569",
                          mb: 0.3,
                          borderBottom: "1px solid #0d1829",
                          pb: 0.3,
                        }}
                      >
                        {hh}
                      </Typography>
                    ))}
                </Box>
              )}
            </CalcInner>
          )}
        </CalcSide>

        {/* MAIN */}
        <MainArea>
          <ScrollPane>
            {/* ── OBJECTIVE ── */}
            {examMode === "objective" && (
              <>
                {totalQ === 0 ? (
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      py: 10,
                      gap: 2,
                    }}
                  >
                    <Typography sx={{ fontSize: "2.5rem" }}>📭</Typography>
                    <Typography
                      sx={{
                        fontFamily: "'Sora',sans-serif",
                        fontWeight: 700,
                        color: "#64748b",
                      }}
                    >
                      No objective questions
                    </Typography>
                  </Box>
                ) : currentQ ? (
                  <>
                    {/* Section label row */}
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        mb: 2,
                      }}
                    >
                      <Typography
                        sx={{
                          fontFamily: "'Sora',sans-serif",
                          fontSize: ".7rem",
                          fontWeight: 700,
                          color: "#64748b",
                          textTransform: "uppercase",
                          letterSpacing: ".08em",
                        }}
                      >
                        {showCorrections ? "Corrections" : "Objective"} · Q
                        {currentIdx + 1} of {totalQ}
                      </Typography>
                      <Box sx={{ display: "flex", gap: 1 }}>
                        {!showCorrections && selectedOptions[currentQ.id] && (
                          <Chip
                            label="Answered ✓"
                            size="small"
                            sx={{
                              background: "#f0fdf4",
                              color: "#16a34a",
                              border: "1px solid #bbf7d0",
                              fontFamily: "'Sora',sans-serif",
                              fontWeight: 700,
                              fontSize: ".64rem",
                            }}
                          />
                        )}
                        {showCorrections && corrMap[currentQ.id] && (
                          <Chip
                            icon={
                              corrMap[currentQ.id].result === "correct" ? (
                                <CheckCircleIcon
                                  sx={{ fontSize: "13px !important" }}
                                />
                              ) : (
                                <CancelIcon
                                  sx={{ fontSize: "13px !important" }}
                                />
                              )
                            }
                            label={
                              corrMap[currentQ.id].result === "correct"
                                ? "Correct"
                                : "Incorrect"
                            }
                            size="small"
                            sx={{
                              background:
                                corrMap[currentQ.id].result === "correct"
                                  ? "#f0fdf4"
                                  : "#fff1f2",
                              color:
                                corrMap[currentQ.id].result === "correct"
                                  ? "#16a34a"
                                  : "#dc2626",
                              border: `1px solid ${
                                corrMap[currentQ.id].result === "correct"
                                  ? "#bbf7d0"
                                  : "#fecdd3"
                              }`,
                              fontFamily: "'Sora',sans-serif",
                              fontWeight: 700,
                              fontSize: ".64rem",
                            }}
                          />
                        )}
                      </Box>
                    </Box>

                    <QCard key={`q-${currentIdx}-${showCorrections}`}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 2,
                          mb: 2.5,
                        }}
                      >
                        <Box
                          sx={{
                            width: 38,
                            height: 38,
                            borderRadius: "11px",
                            background:
                              "linear-gradient(135deg,#1d4ed8,#3b82f6)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            boxShadow: "0 6px 16px -4px rgba(37,99,235,.32)",
                            flexShrink: 0,
                          }}
                        >
                          <Typography
                            sx={{
                              fontFamily: "'Sora',sans-serif",
                              fontWeight: 800,
                              color: "#fff",
                              fontSize: ".85rem",
                            }}
                          >
                            {currentIdx + 1}
                          </Typography>
                        </Box>
                        <Typography
                          sx={{
                            fontFamily: "'Sora',sans-serif",
                            fontSize: ".64rem",
                            fontWeight: 700,
                            color: "#94a3b8",
                            textTransform: "uppercase",
                            letterSpacing: ".07em",
                          }}
                        >
                          Multiple Choice
                        </Typography>
                      </Box>

                      <Box
                        sx={{
                          fontFamily: "'Sora',sans-serif",
                          fontSize: "1rem",
                          color: "#0f172a",
                          lineHeight: 1.82,
                          mb: 3,
                        }}
                      >
                        {renderContent(currentQ.text)}
                      </Box>

                      {currentQ.Eq_img && (
                        <Box
                          sx={{
                            mb: 3,
                            borderRadius: 12,
                            overflow: "hidden",
                            border: "1.5px solid #e2e8f0",
                            maxWidth: 440,
                            cursor: "zoom-in",
                            display: "inline-block",
                          }}
                          onClick={() => setPreviewImg(currentQ.Eq_img)}
                        >
                          <img
                            src={currentQ.Eq_img}
                            alt="question"
                            style={{ width: "100%", display: "block" }}
                          />
                        </Box>
                      )}

                      <Box sx={{ mt: 0.5 }}>
                        {currentQ.options?.map((opt, oi) => {
                          const status = getOptStatus(currentQ, opt);
                          return (
                            <OptBtn
                              key={opt.id}
                              status={status}
                              onClick={() =>
                                !showCorrections &&
                                setSelectedOptions((p) => ({
                                  ...p,
                                  [currentQ.id]: opt.id,
                                }))
                              }
                              sx={{ animationDelay: `${oi * 0.055}s` }}
                            >
                              <OptCircle status={status}>
                                {opt.option_letter}
                              </OptCircle>
                              <Box
                                sx={{
                                  fontFamily: "'Sora',sans-serif",
                                  fontSize: ".92rem",
                                  color: "#334155",
                                  lineHeight: 1.7,
                                  pt: "3px",
                                  flex: 1,
                                }}
                              >
                                {renderContent(opt.text)}
                                {opt.Eoption_img && (
                                  <Box
                                    component="img"
                                    src={opt.Eoption_img}
                                    alt="opt"
                                    sx={{
                                      mt: 1,
                                      maxWidth: 180,
                                      borderRadius: 8,
                                      cursor: "zoom-in",
                                      display: "block",
                                    }}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setPreviewImg(opt.Eoption_img);
                                    }}
                                  />
                                )}
                              </Box>
                              {status === "correct" && (
                                <CheckCircleIcon
                                  sx={{
                                    color: "#22c55e",
                                    fontSize: 18,
                                    flexShrink: 0,
                                    mt: 0.35,
                                  }}
                                />
                              )}
                              {status === "wrong" && (
                                <CancelIcon
                                  sx={{
                                    color: "#ef4444",
                                    fontSize: 18,
                                    flexShrink: 0,
                                    mt: 0.35,
                                  }}
                                />
                              )}
                            </OptBtn>
                          );
                        })}
                      </Box>

                      {showCorrections &&
                        corrMap[currentQ.id] &&
                        corrMap[currentQ.id].result === "incorrect" && (
                          <Box
                            sx={{
                              mt: 2.5,
                              background: "#fff7ed",
                              border: "1.5px solid #fed7aa",
                              borderRadius: 13,
                              p: "14px 18px",
                            }}
                          >
                            <Typography
                              sx={{
                                fontFamily: "'Sora',sans-serif",
                                fontSize: ".75rem",
                                fontWeight: 700,
                                color: "#9a3412",
                                mb: 0.6,
                              }}
                            >
                              ❌ Your answer was incorrect
                            </Typography>
                            <Typography
                              sx={{
                                fontFamily: "'Sora',sans-serif",
                                fontSize: ".84rem",
                                color: "#7c2d12",
                                lineHeight: 1.65,
                              }}
                            >
                              <b>Correct answer:</b>{" "}
                              {Array.isArray(
                                corrMap[currentQ.id].correct_options
                              )
                                ? corrMap[currentQ.id].correct_options.map(
                                    (a, i) => (
                                      <span key={i}>
                                        {renderContent(a)}
                                        {i <
                                          corrMap[currentQ.id].correct_options
                                            .length -
                                            1 && ", "}
                                      </span>
                                    )
                                  )
                                : renderContent(
                                    corrMap[currentQ.id].correct_options
                                  )}
                            </Typography>
                          </Box>
                        )}
                    </QCard>
                  </>
                ) : null}
              </>
            )}

            {/* ── THEORY ── */}
            {examMode === "theory" && (
              <Box
                sx={{
                  animation: `${fadeUp} .3s cubic-bezier(.22,1,.36,1) both`,
                }}
              >
                <Box sx={{ mb: 2 }}>
                  <Typography
                    sx={{
                      fontFamily: "'Sora',sans-serif",
                      fontSize: ".7rem",
                      fontWeight: 700,
                      color: "#64748b",
                      textTransform: "uppercase",
                      letterSpacing: ".08em",
                      mb: 0.5,
                    }}
                  >
                    Theory Section
                  </Typography>
                </Box>
                <Box
                  sx={{
                    background: "#fff",
                    borderRadius: 18,
                    border: "1.5px solid #e8edf5",
                    p: "24px 28px",
                    boxShadow: "0 4px 24px -8px rgba(15,23,42,.08)",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                      mb: 2.2,
                      pb: 2,
                      borderBottom: "1px solid #f1f5f9",
                    }}
                  ></Box>

                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      mt: 2,
                      pt: 2,
                      borderTop: "1px solid #f1f5f9",
                    }}
                  >
                    <Button
                      onClick={() =>
                        navigate(`/waec-theory?subjectId=${subjectId}`)
                      }
                      variant="contained"
                      sx={{
                        borderRadius: 10,
                        textTransform: "none",
                        fontFamily: "'Sora',sans-serif",
                        fontWeight: 700,
                        fontSize: ".8rem",
                        py: 0.9,
                        px: 2,
                        background: "linear-gradient(105deg,#6d28d9,#8b5cf6)",
                        boxShadow: "0 6px 16px -4px rgba(109,40,217,.4)",
                        "&:hover": { transform: "translateY(-2px)" },
                        transition: "all .22s",
                        color: "#fff",
                      }}
                    >
                      view Theory Exam page →
                    </Button>
                  </Box>
                </Box>
              </Box>
            )}

            <Box sx={{ height: 8 }} />
          </ScrollPane>

          {/* BOTTOM NAV — objective only */}
          {examMode === "objective" && totalQ > 0 && (
            <BottomNav>
              {/* Prev / Next */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 1.5,
                }}
              >
                <Button
                  onClick={() => setCurrentIdx((i) => Math.max(0, i - 1))}
                  disabled={currentIdx === 0}
                  startIcon={<ChevronLeftIcon />}
                  sx={{
                    borderRadius: 9,
                    textTransform: "none",
                    fontFamily: "'Sora',sans-serif",
                    fontWeight: 600,
                    fontSize: ".82rem",
                    color: "#64748b",
                    border: "1.5px solid #e2e8f0",
                    px: 2,
                    py: 0.85,
                    "&:hover": {
                      background: "#f1f5f9",
                      borderColor: "#cbd5e1",
                    },
                    "&.Mui-disabled": { opacity: 0.3 },
                  }}
                >
                  Previous
                </Button>
                <Typography
                  sx={{
                    fontFamily: "'Sora',sans-serif",
                    fontSize: ".8rem",
                    fontWeight: 600,
                    color: "#94a3b8",
                  }}
                >
                  {currentIdx + 1}{" "}
                  <Box component="span" sx={{ opacity: 0.45 }}>
                    / {totalQ}
                  </Box>
                </Typography>
                <Button
                  onClick={() =>
                    setCurrentIdx((i) => Math.min(totalQ - 1, i + 1))
                  }
                  disabled={currentIdx === totalQ - 1}
                  endIcon={<ChevronRightIcon />}
                  sx={{
                    borderRadius: 9,
                    textTransform: "none",
                    fontFamily: "'Sora',sans-serif",
                    fontWeight: 700,
                    fontSize: ".82rem",
                    color: "#fff",
                    background: "linear-gradient(105deg,#1d4ed8,#3b82f6)",
                    px: 2,
                    py: 0.85,
                    boxShadow: "0 5px 14px -4px rgba(37,99,235,.35)",
                    "&:hover": {
                      transform: "translateY(-2px)",
                      boxShadow: "0 9px 20px -4px rgba(37,99,235,.45)",
                    },
                    "&.Mui-disabled": {
                      opacity: 0.3,
                      background: "#e2e8f0",
                      color: "#94a3b8",
                      boxShadow: "none",
                    },
                    transition: "all .22s",
                  }}
                >
                  Next
                </Button>
              </Box>

              {/* Number pad */}
              <Box
                sx={{
                  background: "#f8faff",
                  borderRadius: 12,
                  border: "1px solid #eef2fb",
                  p: "10px 14px",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    mb: 1,
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: "'Sora',sans-serif",
                      fontSize: ".62rem",
                      fontWeight: 700,
                      color: "#94a3b8",
                      textTransform: "uppercase",
                      letterSpacing: ".09em",
                    }}
                  >
                    Jump to question
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      gap: 1.5,
                      alignItems: "center",
                      flexWrap: "wrap",
                    }}
                  >
                    {[
                      { color: "#3b82f6", label: "Current" },
                      {
                        color: "#22c55e",
                        label: showCorrections ? "Correct" : "Answered",
                      },
                      ...(showCorrections
                        ? [{ color: "#ef4444", label: "Wrong" }]
                        : []),
                      { color: "#e2e8f0", label: "Pending" },
                    ].map((l) => (
                      <Box
                        key={l.label}
                        sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                      >
                        <Box
                          sx={{
                            width: 8,
                            height: 8,
                            borderRadius: 3,
                            background: l.color,
                            flexShrink: 0,
                          }}
                        />
                        <Typography
                          sx={{
                            fontFamily: "'Sora',sans-serif",
                            fontSize: ".6rem",
                            color: "#94a3b8",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {l.label}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: "6px",
                    pt: "2px",
                  }}
                >
                  {questions.map((q, i) => {
                    const corr = corrMap[q.id];
                    return (
                      <NavDot
                        key={q.id}
                        isanswered={!!selectedOptions[q.id] ? "true" : "false"}
                        iscurrent={i === currentIdx ? "true" : "false"}
                        iscorrect={
                          showCorrections && corr?.result === "correct"
                            ? "true"
                            : "false"
                        }
                        iswrong={
                          showCorrections && corr?.result === "incorrect"
                            ? "true"
                            : "false"
                        }
                        onClick={() => setCurrentIdx(i)}
                      >
                        {i + 1}
                      </NavDot>
                    );
                  })}
                </Box>
              </Box>
            </BottomNav>
          )}
        </MainArea>
      </Body>

      {/* RESULT MODAL */}
      {showModal && (
        <Box
          sx={{
            position: "fixed",
            inset: 0,
            background: "rgba(7,16,31,.82)",
            backdropFilter: "blur(12px)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
            p: 2,
          }}
        >
          <Box
            sx={{
              background: "#fff",
              borderRadius: "26px",
              p: "40px 38px",
              textAlign: "center",
              width: "100%",
              maxWidth: 410,
              boxShadow: "0 48px 96px -24px rgba(15,23,42,.35)",
              border: "1px solid #e2e8f0",
              animation: `${popIn} .4s cubic-bezier(.22,1,.36,1) both`,
            }}
          >
            <ScoreRing pct={pct}>
              <Box sx={{ position: "relative", zIndex: 1 }}>
                <Typography
                  sx={{
                    fontFamily: "'Sora',sans-serif",
                    fontWeight: 800,
                    fontSize: "1.5rem",
                    color: pct >= 50 ? "#15803d" : "#dc2626",
                    lineHeight: 1,
                  }}
                >
                  {score?.correct}
                </Typography>
                <Typography
                  sx={{
                    fontFamily: "'Sora',sans-serif",
                    fontSize: ".64rem",
                    color: "#94a3b8",
                    fontWeight: 700,
                  }}
                >
                  of {score?.total}
                </Typography>
              </Box>
            </ScoreRing>
            <Typography
              sx={{
                fontFamily: "'Sora',sans-serif",
                fontWeight: 800,
                fontSize: "1.25rem",
                color: "#0f172a",
                mb: 0.5,
              }}
            >
              Exam Submitted!
            </Typography>
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 0.8,
                background: pct >= 50 ? "#f0fdf4" : "#fff1f2",
                border: `1px solid ${pct >= 50 ? "#bbf7d0" : "#fecdd3"}`,
                borderRadius: 100,
                px: 1.8,
                py: 0.6,
                mb: 0.8,
              }}
            >
              <Typography
                sx={{
                  fontFamily: "'Sora',sans-serif",
                  fontWeight: 800,
                  fontSize: ".95rem",
                  color: pct >= 50 ? "#16a34a" : "#dc2626",
                }}
              >
                {pct}%
              </Typography>
              <Typography
                sx={{
                  fontFamily: "'Sora',sans-serif",
                  fontSize: ".78rem",
                  color: pct >= 50 ? "#15803d" : "#b91c1c",
                }}
              >
                {pct >= 70
                  ? "Excellent! 🎉"
                  : pct >= 50
                  ? "Good effort!"
                  : "Keep studying"}
              </Typography>
            </Box>
            <Typography
              sx={{
                fontFamily: "'Sora',sans-serif",
                fontSize: ".78rem",
                color: "#94a3b8",
                mb: 3,
              }}
            >
              {score?.correct} correct ·{" "}
              {(score?.total || 0) - (score?.correct || 0)} incorrect ·{" "}
              {score?.total} total
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.2 }}>
              <Button
                onClick={() => {
                  setShowModal(false);
                  setShowCorrections(true);
                  setCurrentIdx(0);
                  setExamMode("objective");
                }}
                fullWidth
                variant="contained"
                sx={{
                  borderRadius: "11px",
                  textTransform: "none",
                  fontFamily: "'Sora',sans-serif",
                  fontWeight: 700,
                  py: 1.4,
                  fontSize: ".88rem",
                  background: "linear-gradient(105deg,#1d4ed8,#3b82f6)",
                  boxShadow: "0 8px 20px -6px rgba(37,99,235,.4)",
                  "&:hover": { transform: "translateY(-2px)" },
                  transition: "all .22s",
                }}
              >
                View Corrections
              </Button>
              <Button
                onClick={() => navigate(`/ntheory?subjectId=${subjectId}`)}
                fullWidth
                variant="outlined"
                sx={{
                  borderRadius: "11px",
                  textTransform: "none",
                  fontFamily: "'Sora',sans-serif",
                  fontWeight: 700,
                  py: 1.4,
                  fontSize: ".88rem",
                  color: "#7c3aed",
                  borderColor: "#c4b5fd",
                  borderWidth: 1.5,
                  "&:hover": { background: "#f5f3ff" },
                }}
              >
                Proceed to Theory →
              </Button>
              <Button
                onClick={() => navigate(-1)}
                fullWidth
                sx={{
                  borderRadius: "11px",
                  textTransform: "none",
                  fontFamily: "'Sora',sans-serif",
                  fontWeight: 600,
                  py: 1.1,
                  fontSize: ".84rem",
                  color: "#94a3b8",
                  "&:hover": { background: "#f8faff" },
                }}
              >
                Go Back
              </Button>
            </Box>
          </Box>
        </Box>
      )}

      {/* IMAGE PREVIEW */}
      {previewImg && (
        <Box
          onClick={() => setPreviewImg(null)}
          sx={{
            position: "fixed",
            inset: 0,
            background: "rgba(7,16,31,.9)",
            backdropFilter: "blur(14px)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 10000,
            cursor: "zoom-out",
            p: 3,
          }}
        >
          <Box
            component="img"
            src={previewImg}
            alt="preview"
            sx={{
              maxWidth: "90%",
              maxHeight: "86vh",
              borderRadius: "14px",
              boxShadow: "0 32px 80px rgba(0,0,0,.55)",
              objectFit: "contain",
            }}
          />
        </Box>
      )}
    </Root>
  );
}
